import { Box } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import { useParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../components/CustomDataGridV2";
import Flex from "../../../../components/Flex";
import Text from "../../../../components/Text";
import ToggleSwitch from "../../../../components/ToggleSwitch";
import { GENDER_RECORD } from "../../../../constants";
import { PatientDataType } from "../../../../dummy-data/patient-data";
import { usePatientsList } from "../../../../queries/use-get-patients";
import { useChangePatientStatus } from "../../../../queries/use-patient-status";
import { formatDate } from "../../../../utils/date-time-utility";
import {
  DEFAULT_PAGE_SIZE,
  DGT_Pagination,
  applyDefaultColumnSettings,
  formatPhoneNumber,
  getAge,
} from "../../../../utils/utils";
import { getStoredRealm } from "@/utils/auth-storage";

interface Props {
  locationUuid: string;
}

const LocationPatient = ({ locationUuid }: Props) => {
  const [pagination, setPagination] = useState<DGT_Pagination>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const { mutateAsync: changeLocationStatus } = useChangePatientStatus();

  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const { providerGroupId } = useParams();
  const { data: patientList, refetch } = usePatientsList({
    gridSorting,
    pagination,
    searchKey: "",
    providerGroupId: providerGroupId ?? getStoredRealm(),
    locationUuid: locationUuid,
  });

  const COLUMNS = applyDefaultColumnSettings<PatientDataType>([
    {
      field: "uuid",
      flex: 0.5,
      disableColumnMenu: true,
      headerName: "Patient ID",
      sortable: false,
      renderCell: ({ row }) => {
        return row?.ecarehealthPatientId ?? "-";
        // return <Text>{row?.uuid?.split("-")?.[0]?.toUpperCase()}</Text>;
      },
    },

    {
      field: "name",
      flex: 0.8,
      minWidth: 200,
      disableColumnMenu: true,
      headerName: "Patient Name",
      renderCell: ({ row }) => {
        return (
          <Box>
            <Flex gap={"20px"}>
              <Flex gap={0.5}>
                <Text fontWeight={"medium"} marginBottom={"3px"}>
                  {`${row?.firstName} ${row?.lastName} `}
                </Text>
                <Text variant="subtitle2">{`(${getAge(row?.birthDate)} yrs-${
                  GENDER_RECORD[row.gender]
                })`}</Text>
              </Flex>
            </Flex>
          </Box>
        );
      },
      // todo
      // temparory sort false because B.E not accepting key giving 500
      sortable: false,
    },
    {
      field: "birthDate",
      flex: 0.6,
      minWidth: 100,
      headerName: "Date Of Birth",
      sortable: false,
      renderCell: ({ row }) => row?.birthDate && formatDate(row?.birthDate),
    },
    {
      field: "mobileNumber",
      flex: 0.8,
      minWidth: 200,
      headerName: "Contact",
      renderCell: ({ ...params }) => {
        return (
          <Text marginBottom={"3px"}>
            {params.value ? formatPhoneNumber(params.value) : "-"}
          </Text>
        );
      },
      sortable: false,
    },
    {
      field: "email",
      flex: 0.5,
      minWidth: 100,
      headerName: "Email",
      sortable: false,
    },
    {
      field: "provider",
      flex: 0.8,
      minWidth: 160,
      headerName: "Primary Care Provider",
      renderCell: ({ ...params }) => {
        return (
          <Box>
            <Text marginBottom={"3px"}>
              {params.value?.firstName}
              {params.value?.lastName}
            </Text>
          </Box>
        );
      },
      sortable: false,
    },
    {
      field: "lastVisit",
      flex: 0.6,
      minWidth: 160,
      headerName: "Last Visit",
      sortable: false,
    },
    {
      field: "active",
      flex: 0.5,
      width: 150,
      headerName: "Status",
      sortable: false,
      renderCell: ({ row, value }) => (
        <ToggleSwitch
          id={row.uuid.toString()}
          checked={value}
          onConfirm={(status, id) =>
            changeLocationStatus({
              patientId: id as string,
              status,
              xTENANTID: providerGroupId ?? (getStoredRealm() as any),
            }).then((res) => {
              refetch();
              return res;
            })
          }
        />
      ),
    },
  ]);

  return (
    <Box sx={{ height: "calc(100vh - 7rem - 200px)" }}>
      <CustomDataGridV2
        getRowId={(row) => row.uuid}
        rows={patientList?.data?.content ?? []}
        rowCount={patientList?.data?.page?.totalElements ?? 10}
        columns={COLUMNS}
        rowHeight={70}
        disableRowSelectionOnClick
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
        }
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        onSortModelChange={setGridSorting}
      />
    </Box>
  );
};

export default LocationPatient;
